/**
 * Resolves a relative daterange filter value to absolute { from, to } dates.
 * If the value is already absolute (no _relative flag), returns it unchanged.
 *
 * @param {Object} value - Parsed filter value object
 * @returns {Object} - { from: string|null, to: string|null }
 */
const ukDateParts = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

export function resolveRelativeDateRange(value) {
  if (!value || !value._relative) return value;

  const { direction, amount, unit } = value;
  const n = Number(amount);
  // Blank / 0 amount: no date limit (it used to mean 'today' by accident)
  if (!Number.isFinite(n) || n < 1) return { from: null, to: null };

  // Today's UK calendar date, then pure calendar maths in UTC so the
  // computer's own time zone cannot move the day.
  const parts = ukDateParts.formatToParts(new Date());
  const get = type => Number(parts.find(p => p.type === type).value);
  const boundary = new Date(Date.UTC(get('year'), get('month') - 1, get('day')));

  if (unit === 'days') boundary.setUTCDate(boundary.getUTCDate() - n);
  else if (unit === 'weeks') boundary.setUTCDate(boundary.getUTCDate() - n * 7);
  else if (unit === 'months') {
    // 31 Mar minus 1 month = 28/29 Feb, not 3 Mar (same as Postgres interval maths)
    const day = boundary.getUTCDate();
    boundary.setUTCDate(1);
    boundary.setUTCMonth(boundary.getUTCMonth() - n);
    const lastDay = new Date(
      Date.UTC(boundary.getUTCFullYear(), boundary.getUTCMonth() + 1, 0)
    ).getUTCDate();
    boundary.setUTCDate(Math.min(day, lastDay));
  }

  const boundaryISO = boundary.toISOString().slice(0, 10);

  if (direction === 'older') return { from: null, to: boundaryISO };
  if (direction === 'recent') return { from: boundaryISO, to: null };

  return value;
}
